import React, { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import { Card, Row, Col, Button } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { setFilters } from "../SalaryPaymentReportSlice";
import {
  fetchSalaryPaymentReport,
  fetchReportEmployees,
} from "../SalaryPaymentReportThunk";
import { useDefaultDateRange } from "../../../hooks/useDefaultDateRange";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import {
  fetchEmployees,
  fetchUsers,
} from "../../../store/commonThunks/lookups";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";

const SalaryPaymentReportFilters = ({ messageApi }) => {
  const dispatch = useDispatch();
  const { filters } = useSelector((state) => state.salaryPaymentReport);
  const lookups = useSelector((state) => state.lookups);

  const { employees } = useSelector((state) => state.salaryPaymentReport);

  const payType = [
    { pay_type: "All", db_value: "All"},
    { pay_type: "Monthly Salary", db_value: "Monthly Payment" },
    { pay_type: "Basic Salary", db_value: "Basic Salary" },
    { pay_type: "Advance Salary", db_value: "Advance Salary" },
    { pay_type: "Overtime", db_value: "Overtime" },
    { pay_type: "Flight Ticket Reimbursement", db_value: "Flight Ticket Reimbursement" },
    { pay_type: "Commission", db_value: "Commission" },
    { pay_type: "Bonus", db_value: "Bonus" },
    { pay_type: "Deduction", db_value: "Deduction" },
    { pay_type: "Conveyance", db_value: "Conveyance" },
    { pay_type: "TA", db_value: "TA" },
    { pay_type: "DA", db_value: "DA" },
    { pay_type: "MA", db_value: "MA" },
  ];

  const searchEmployee = useCallback(
    (text) => dispatch(fetchReportEmployees({ query: text })),
    [dispatch]
  );

  useDefaultDateRange(setFilters, filters, "today");

  useEffect(() => {
    dispatch(fetchEmployees());
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    if (
      filters.filterType &&
      filters.recordType &&
      filters.fromDate &&
      filters.toDate
    ) {
      handleReport();
    }
  }, [filters]);

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.filterType) {
      messageApi.warning("Select Filter Type");
      return;
    }

    const payload = {
      selectedFilterType: filters.filterType,
      selectedRecordType: filters.recordType,
      filters,
    };

    dispatch(fetchSalaryPaymentReport(payload)).then((res) => {
      if (res.meta.requestStatus === "fulfilled") {
        messageApi.success("Report loaded ✅");
      } else {
        messageApi.error("Failed to fetch records ❌");
      }
    });
  };

  return (
    <Card className="page-filters mb-4 shadow-md rounded-xl">
      <Row gutter={[16, 16]} style={{ alignItems: "center" }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
          <label>Filter Type</label>
          <SearchListAutoComplete
            optionsList={[
              { sale_id: "All", display_text: "All" },
              { sale_id: "By Employee", display_text: "By Employee" },
              { sale_id: "By Type", display_text: "By Type" },
              { sale_id: "By User", display_text: "By User" },
            ]}
            value={{ value: filters.filterType, label: filters.filterType }}
            onSelect={(obj) => handleFilterChange("filterType", obj?.sale_id)}
            placeholder="Filter Type"
            showAddNew={false}
          />
        </Col>

        {filters.filterType === "By Employee" && (
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
            <label>Employee</label>
            <SearchListAutoComplete
              searchThunk={searchEmployee}
              resultSelector={(state) => state.salaryPaymentReport.employees}
              onSelect={(obj) =>
                handleFilterChange("employeeId", obj?.employee_id)
              }
              placeholder="Select Employee"
              showAddNew={false}
            />
          </Col>
        )}

        {filters.filterType === "By User" && (
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
            <label>User</label>
            <SearchListAutoComplete
              optionsList={lookups.users.map((u) => ({
                sale_id: u.id,
                display_text: u.full_name,
              }))}
              onSelect={(obj) => handleFilterChange("userId", obj?.sale_id)}
              placeholder="Select User"
              showAddNew={false}
            />
          </Col>
        )}

        {["By Type", "By Employee"].includes(filters.filterType) && (
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
            <label>Type</label>
            <SearchListAutoComplete
              optionsList={payType.map((t) => ({
                display_text: t.pay_type,
                sale_id: t.db_type,
              }))}
              onSelect={(obj) =>
                handleFilterChange("typeId", obj?.display_text)
              }
              placeholder="Select User"
              showAddNew={false}
            />
          </Col>
        )}
        {["All", "By User"].includes(filters.filterType) && (
          <Col xs={24} sm={12} md={8} lg={6} xl={5} xxl={4}>
            <label>Record Type</label>
            <SearchListAutoComplete
              optionsList={[
                { sale_id: "without details", display_text: "Without Details" },
                { sale_id: "with details", display_text: "With Details" },
              ]}
              value={filters.recordType ? {
                value: filters.recordType,
                label:
                  filters.recordType === "with details"
                    ? "With Details"
                    : "Without Details",
              }:undefined
            }
              onSelect={(obj) => {
              if(!obj) {
                handleFilterChange("recordType", null);
                return;
              } 
              handleFilterChange("recordType", obj?.sale_id);
            }}
              placeholder="Record Type"
              showAddNew={false}
            />
          </Col>
        )}
        <CommonDateRangeFilter
          filters={filters}
          onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
          }}
        />

        <Col
          xs={24}
          sm={12}
          md={9}
          lg={6}
          xl={3}
          style={{ display: "flex", alignItems: "flex-end", height: 58 }}
        >
          <Button
            type="primary"
            icon={<SearchOutlined />}
            onClick={handleReport}
            style={{height: 35.19}}
            block
          >
            Submit
          </Button>
        </Col>
      </Row>
      <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .page-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
  `}</style>
    </Card>
  );
};

export default SalaryPaymentReportFilters;
